import { c as _c } from "react/compiler-runtime";
import { PreservingNavigate } from "@components/common/preserve-navigation/preserving-navigate";
import { TabsWithRoutes } from "@components/common/tabs/tabs-with-routes";
import { isGroupDetails } from "@custom-types/type-guards";
import { useHasUserValidRoleGroupLevel } from "@hooks/access-control/use-has-user-valid-role-group-level";
import { ChangeGroupThumbnailButton } from "@pages/group-details/change-group-thumbnail-button";
import { DeleteGroupButton } from "@pages/group-details/delete-group-button";
import { GroupManagers } from "@pages/group-details/group-managers";
import { GroupProjects } from "@pages/group-details/group-projects";
import { NotFoundPage } from "@pages/not-found-page";
import { GroupTabs, MainRoutes } from "@router/route-params";
import { useAppParams } from "@router/router-helper";
import { useCompanyId } from "@context-providers/company-id-provider";
import { selectedGroupSelector } from "@store/groups/groups-selector";
import { setSelectedGroupId } from "@store/groups/groups-slice";
import { fetchGroupDetails, fetchGroupProjectsCount } from "@store/groups/groups-slice-thunk";
import { transientProjectsTotalCountSelector } from "@store/projects/projects-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { bulkActionNameSelector } from "@store/table/table-selector";
import { RequiredFeatureCompanyLevelName } from "@utils/feature-control/company/company-feature-control-types";
import { OpenTabEvents } from "@utils/track-event/track-event-list";
import { useEffect, useMemo } from "react";
import { Stack } from "@mui-wrapper";
import { UtilsTypes } from "@stellar/utils";

/**
 * Group details page
 */
export function GroupDetails() {
  const $ = _c(57);
  const {
    groupId,
    groupTabs
  } = useAppParams();
  const companyId = useCompanyId();
  const dispatch = useAppDispatch();
  const selectedGroup = useAppSelector(selectedGroupSelector);
  const bulkActionName = useAppSelector(bulkActionNameSelector);
  const groupProjectsTotalCount = useAppSelector(transientProjectsTotalCountSelector);
  let t0;
  if ($[0] !== bulkActionName || $[1] !== companyId || $[2] !== dispatch || $[3] !== groupId) {
    t0 = () => {
      if (groupId && bulkActionName === null) {
        dispatch(fetchGroupDetails({
          companyId,
          groupId
        }));
        dispatch(fetchGroupProjectsCount({
          companyId,
          groupId
        }));
      }
    };
    $[0] = bulkActionName;
    $[1] = companyId;
    $[2] = dispatch;
    $[3] = groupId;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== bulkActionName || $[6] !== companyId || $[7] !== dispatch || $[8] !== groupId || $[9] !== groupProjectsTotalCount) {
    t1 = [companyId, dispatch, groupId, bulkActionName, groupProjectsTotalCount];
    $[5] = bulkActionName;
    $[6] = companyId;
    $[7] = dispatch;
    $[8] = groupId;
    $[9] = groupProjectsTotalCount;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[11] !== dispatch) {
    t2 = () => () => {
      dispatch(setSelectedGroupId(null));
    };
    t3 = [dispatch];
    $[11] = dispatch;
    $[12] = t2;
    $[13] = t3;
  } else {
    t2 = $[12];
    t3 = $[13];
  }
  useEffect(t2, t3);
  let t4;
  if ($[14] !== selectedGroup) {
    t4 = selectedGroup && isGroupDetails(selectedGroup) ? selectedGroup : null;
    $[14] = selectedGroup;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const group = t4;
  let t5;
  if ($[16] !== group) {
    t5 = {
      selectedGroup: group
    };
    $[16] = group;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const {
    canEditGroupDetails,
    canDeleteMemberFromGroup
  } = useHasUserValidRoleGroupLevel(t5);
  let t6;
  bb0: {
    if (!group || group.id !== groupId) {
      t6 = undefined;
      break bb0;
    }
    if (!canEditGroupDetails && !canDeleteMemberFromGroup) {
      t6 = undefined;
      break bb0;
    }
    let t7;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        alignItems: "center",
        gap: 1
      };
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] !== canEditGroupDetails) {
      t8 = canEditGroupDetails && <ChangeGroupThumbnailButton />;
      $[19] = canEditGroupDetails;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    let t9;
    if ($[21] !== canDeleteMemberFromGroup || $[22] !== group) {
      t9 = canDeleteMemberFromGroup && <DeleteGroupButton group={group} />;
      $[21] = canDeleteMemberFromGroup;
      $[22] = group;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    let t10;
    if ($[24] !== t8 || $[25] !== t9) {
      t10 = <Stack direction="row" sx={t7}>{t8}{t9}</Stack>;
      $[24] = t8;
      $[25] = t9;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    t6 = t10;
  }
  const actionButton = t6;
  if (groupTabs === GroupTabs.projects && groupId) {
    const t7 = `/${companyId}/${MainRoutes.groups}/${groupId}/${GroupTabs.activeProjects}`;
    let t8;
    if ($[27] !== t7) {
      t8 = <PreservingNavigate to={t7} />;
      $[27] = t7;
      $[28] = t8;
    } else {
      t8 = $[28];
    }
    return t8;
  }
  if (!groupTabs || !Object.values(GroupTabs).includes(groupTabs) || !groupId) {
    let t7;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <NotFoundPage />;
      $[29] = t7;
    } else {
      t7 = $[29];
    }
    return t7;
  }
  let t7;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <GroupManagers isLoading={true} />;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== actionButton) {
    t8 = {
      label: "Members",
      route: GroupTabs.managers,
      content: _temp,
      loadingContent: t7,
      requiredFeatureCompanyLevel: RequiredFeatureCompanyLevelName.canViewCompanyGroups,
      actionButtons: actionButton
    };
    $[31] = actionButton;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t10;
  let t9;
  if ($[33] !== companyId || $[34] !== groupId) {
    t9 = group_1 => <GroupProjects companyId={companyId} groupId={groupId} group={group_1} projectState={UtilsTypes.ProjectArchivingState.active} />;
    t10 = <GroupProjects companyId={companyId} groupId={groupId} isLoading={true} projectState={UtilsTypes.ProjectArchivingState.active} />;
    $[33] = companyId;
    $[34] = groupId;
    $[35] = t10;
    $[36] = t9;
  } else {
    t10 = $[35];
    t9 = $[36];
  }
  let t11;
  if ($[37] !== actionButton || $[38] !== t10 || $[39] !== t9) {
    t11 = {
      label: "Active projects",
      route: GroupTabs.activeProjects,
      content: t9,
      loadingContent: t10,
      requiredFeatureCompanyLevel: RequiredFeatureCompanyLevelName.canViewCompanyGroups,
      actionButtons: actionButton
    };
    $[37] = actionButton;
    $[38] = t10;
    $[39] = t9;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  let t12;
  let t13;
  if ($[41] !== companyId || $[42] !== groupId) {
    t12 = group_2 => <GroupProjects companyId={companyId} groupId={groupId} group={group_2} projectState={UtilsTypes.ProjectArchivingState.archived} />;
    t13 = <GroupProjects companyId={companyId} groupId={groupId} isLoading={true} projectState={UtilsTypes.ProjectArchivingState.archived} />;
    $[41] = companyId;
    $[42] = groupId;
    $[43] = t12;
    $[44] = t13;
  } else {
    t12 = $[43];
    t13 = $[44];
  }
  let t14;
  if ($[45] !== actionButton || $[46] !== t12 || $[47] !== t13) {
    t14 = {
      label: "Archived projects",
      route: GroupTabs.archivedProjects,
      content: t12,
      loadingContent: t13,
      requiredFeatureCompanyLevel: RequiredFeatureCompanyLevelName.canViewCompanyGroups,
      actionButtons: actionButton
    };
    $[45] = actionButton;
    $[46] = t12;
    $[47] = t13;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[49] !== t11 || $[50] !== t14 || $[51] !== t8) {
    t15 = [t8, t11, t14];
    $[49] = t11;
    $[50] = t14;
    $[51] = t8;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== group || $[54] !== groupTabs || $[55] !== t15) {
    t16 = <TabsWithRoutes selectedTab={groupTabs} selectedProject={null} dataTestId="GROUP_DETAILS_CONTAINER" trackingEventPageName={OpenTabEvents.openGroupTab} requiredAttribute={group} tabs={t15} />;
    $[53] = group;
    $[54] = groupTabs;
    $[55] = t15;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  return t16;
}
function _temp(group_0) {
  return <GroupManagers group={group_0} />;
}